import React from 'react';
import { resolveMedia, isVideoUrl } from '../../lib/media';
import UploadMediaButton from './UploadMediaButton';
import { uid } from '../../lib/uid';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Crown, RefreshCw, ArrowUp, ArrowDown, Trash2, Plus, Check, Loader2 } from 'lucide-react';

const WallOfFamePanel = ({ items, setItems, onSave, onReload, saving, loaded }) => {
  const update = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Caricamento Wall of Fame...
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }} data-testid="wall-of-fame-panel">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-white font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
            <Crown size={18} className="text-yellow-500" /> WALL OF FAME
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Titolo, anno, media (carica un file o incolla l'URL di immagine/GIF/video .mp4) e link (Instagram, YouTube o Spotify). L'ordine qui è l'ordine in pagina.
          </p>
        </div>
        <Button onClick={onReload} size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800" data-testid="wall-reload-btn">
          <RefreshCw size={14} />
        </Button>
      </div>

      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it._uid || idx} className="flex items-start gap-2 p-2 rounded border border-gray-800 bg-gray-900/50" data-testid={`wall-item-${idx}`}>
            <span className="text-gray-500 text-xs w-6 text-center font-mono pt-2">{idx + 1}</span>
            <div className="w-12 h-[85px] shrink-0 rounded overflow-hidden bg-black border border-gray-800">
              {it.src && (isVideoUrl(it.src)
                ? <video src={resolveMedia(it.src)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                : <img src={resolveMedia(it.src)} alt="" className="w-full h-full object-cover" />)}
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_80px] gap-1.5 min-w-0">
              <Input data-testid={`wall-caption-${idx}`} value={it.caption || ''} onChange={e => update(idx, 'caption', e.target.value)}
                placeholder="NOME - Vincitore" className="bg-gray-900 border-gray-700 text-white font-bold text-sm" />
              <Input data-testid={`wall-year-${idx}`} value={it.year || ''} onChange={e => update(idx, 'year', e.target.value)}
                placeholder="2026" className="bg-gray-900 border-gray-700 text-white text-sm" />
              <div className="flex gap-1.5 sm:col-span-2">
                <Input data-testid={`wall-src-${idx}`} value={it.src || ''} onChange={e => update(idx, 'src', e.target.value)}
                  placeholder="URL immagine / GIF / video .mp4 — oppure CARICA" className="bg-gray-900 border-gray-700 text-white font-mono text-xs flex-1" />
                <UploadMediaButton idx={`wall-${idx}`} onUploaded={(url) => update(idx, "src", url)} />
              </div>
              <Input data-testid={`wall-link-${idx}`} value={it.spotifyUrl || ''} onChange={e => update(idx, 'spotifyUrl', e.target.value)}
                placeholder="Link Instagram / YouTube / Spotify (icona in alto a destra)" className="bg-gray-900 border-gray-700 text-white font-mono text-xs sm:col-span-2" />
            </div>
            <div className="flex flex-col gap-1">
              <button type="button" data-testid={`wall-up-${idx}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30"><ArrowUp size={13} /></button>
              <button type="button" data-testid={`wall-down-${idx}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30"><ArrowDown size={13} /></button>
              <button type="button" data-testid={`wall-delete-${idx}`} onClick={() => setItems(items.filter((_, i) => i !== idx))}
                className="p-1.5 rounded bg-red-900/50 hover:bg-red-800 text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <Button data-testid="wall-add-btn" onClick={() => setItems([...items, { _uid: uid(), caption: '', year: '', src: '', spotifyUrl: '' }])}
          size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
          <Plus size={14} className="mr-1" /> AGGIUNGI CARD
        </Button>
        <Button data-testid="wall-save-btn" onClick={onSave} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700 text-black font-bold">
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
          SALVA WALL OF FAME
        </Button>
      </div>
    </div>
  );
};

export default WallOfFamePanel;
